// ABOUTME: React Query hook for fetching truck settings configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/trucks, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {TruckSettingsApiResponse, TruckSettingsConfig, TruckSettingsFormValues} from '../types/truckSettings'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

const transformResponse = (response: TruckSettingsApiResponse, parentValues?: TruckSettingsFormValues): TruckSettingsConfig => {
  const isDefaultTruckSizeOverridden = response.defaultTruckSize?.isOverridden ?? false
  const isAvailableTrucksOverridden = response.availableTrucks?.isOverridden ?? false
  const availableTrucks = response.availableTrucks?.value
    ? {
        truckSizeUomId: response.availableTrucks.value.truckSizeUom?.id ?? '',
        defaultTruckCode: response.availableTrucks.value.defaultTruckCode ?? '',
        trucks: (response.availableTrucks.value.trucks ?? []).map((t) => ({
          code: t.code,
          description: t.description,
          truckSize: t.truckSize,
          orderable: t.orderable,
        })),
      }
    : null
  const defaultTruckSize = response.defaultTruckSize?.value ?? null
  const ownFormValues: TruckSettingsFormValues = {defaultTruckSize, availableTrucks}

  return {
    id: response.id,
    defaultTruckSize,
    availableTrucks,
    truckSizeUomOption: response.availableTrucks?.value?.truckSizeUom
      ? {
          value: response.availableTrucks.value.truckSizeUom.id,
          label: response.availableTrucks.value.truckSizeUom.displaySymbol || response.availableTrucks.value.truckSizeUom.name,
        }
      : null,
    isDefaultTruckSizeOverridden,
    isAvailableTrucksOverridden,
    isTruckTypeSelectable: response.truckTypeSelectable,
    parentValues: parentValues ?? ownFormValues,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !isDefaultTruckSizeOverridden && !isAvailableTrucksOverridden,
      resolvedFrom: null,
      createdAt: response._meta?.createdAt ?? '',
      updatedAt: response._meta?.updatedAt ?? '',
      createdBy: response._meta?.createdBy ?? '',
      updatedBy: response._meta?.updatedBy ?? '',
    },
  }
}

export const useTruckSettingsConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: TruckSettingsConfig | undefined; isLoading: boolean; isFetching: boolean; error: Error | null} => {
  const normalizedBusinessLine = businessLine?.toUpperCase()
  const {data: rawData, isLoading, isFetching, error} = useQuery<TruckSettingsApiResponse[], Error>({
    queryKey: ['trucksConfig', countryId, normalizedBusinessLine, scopeId, rootScopeId],
    queryFn: async () => {
      const res = await api.get<TruckSettingsApiResponse[]>('/tuc-configs/trucks', {
        params: {
          countryId,
          businessLine: normalizedBusinessLine,
          scopeId,
          rootScopeId,
          resolve: true,
        },
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRawConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const rootConfig = rootRawConfig ? transformResponse(rootRawConfig) : undefined
    const parentValues = rootConfig
      ? {defaultTruckSize: rootConfig.defaultTruckSize, availableTrucks: rootConfig.availableTrucks}
      : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      const config = transformResponse(match, parentValues)
      if (!config.truckSizeUomOption) {
        const rootUomOption = rootConfig?.truckSizeUomOption ?? null
        return rootUomOption ? {...config, truckSizeUomOption: rootUomOption} : config
      }
      return config
    }

    if (!rootRawConfig) return asNotInherited(transformResponse(emptyApiRow<TruckSettingsApiResponse>(scopeId)))

    const inherited = transformResponse(rootRawConfig)
    return {
      ...inherited,
      id: undefined,
      _meta: {...inherited._meta, isInherited: true},
      isDefaultTruckSizeOverridden: false,
      isAvailableTrucksOverridden: false,
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, isFetching, error}
}
